import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

interface Company {
  id: number;
  company_name: string;
}

interface ExpenseHead {
  id: number;
  name: string;
  company_id?: number | null;
}

interface StatementRow {
  id: number;
  date_str: string;
  particulars: string;
  debit: number;
  credit: number;
  mapped_invoice_id?: number | null;
  mapped_purchase_id?: number | null;
  mapped_loan_id?: number | null;
  mapped_expense_head_id?: number | null;
  mapped_fd_id?: number | null;
  company_id?: number | null;
}

interface Invoice {
  id: number;
  Company_id: number;
  Total_Amount: number;
  Date: string;
}

interface PurchaseInvoice {
  id: number;
  Company_id: number;
  Total_Amount: number;
  Date: string;
}

const ProfitLossReport: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [expenseHeads, setExpenseHeads] = useState<ExpenseHead[]>([]);
  const [rows, setRows] = useState<StatementRow[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [purchases, setPurchases] = useState<PurchaseInvoice[]>([]);
  
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [compRes, headRes, rowsRes, invRes, purRes] = await Promise.all([
        axios.get(`${API_URL}/auth/companies`),
        axios.get(`${API_URL}/auth/expense-heads`),
        axios.get(`${API_URL}/auth/bank-statement-rows`),
        axios.get(`${API_URL}/auth/invoices`),
        axios.get(`${API_URL}/auth/purchase-invoices`)
      ]);
      setCompanies(compRes.data || []);
      setExpenseHeads(headRes.data || []);
      setRows(rowsRes.data || []);
      setInvoices(invRes.data || []);
      setPurchases(purRes.data || []);
    } catch (err) {
      console.error("Failed to load profit/loss report data:", err);
    } finally {
      setLoading(false);
    }
  };

  const getYearFromDate = (dateStr: string): string => {
    if (!dateStr) return "";
    const parts = dateStr.split(/[-/\s.]+/);
    if (parts.length >= 3) {
      let yr = parts[2];
      if (parts[0].length === 4) yr = parts[0];
      return yr.length === 2 ? "20" + yr : yr;
    }
    return "";
  };

  const availableYears = useMemo(() => {
    const years = new Set<string>();
    rows.forEach(r => {
      const yr = getYearFromDate(r.date_str);
      if (yr) years.add(yr);
    });
    invoices.forEach(i => {
      const yr = getYearFromDate(i.Date);
      if (yr) years.add(yr);
    });
    return Array.from(years).sort((a, b) => b.localeCompare(a));
  }, [rows, invoices]);

  const plData = useMemo(() => {
    const filteredInvoices = invoices.filter(inv => {
      const matchComp = !selectedCompanyId || String(inv.Company_id) === String(selectedCompanyId);
      const matchYear = !selectedYear || getYearFromDate(inv.Date) === selectedYear;
      return matchComp && matchYear;
    });

    const filteredPurchases = purchases.filter(pur => {
      const matchComp = !selectedCompanyId || String(pur.Company_id) === String(selectedCompanyId);
      const matchYear = !selectedYear || getYearFromDate(pur.Date) === selectedYear;
      return matchComp && matchYear;
    });

    const filteredRows = rows.filter(r => {
      const matchComp = !selectedCompanyId || String(r.company_id) === String(selectedCompanyId);
      const matchYear = !selectedYear || getYearFromDate(r.date_str) === selectedYear;
      return matchComp && matchYear;
    });

    // 1. Revenues
    const revenueSales = filteredInvoices.reduce((sum, inv) => sum + Number(inv.Total_Amount || 0), 0);
    const revenueDirectCredits = filteredRows
      .filter(r => r.credit > 0 && !r.mapped_invoice_id)
      .reduce((sum, r) => sum + Number(r.credit || 0), 0);
    const totalRevenue = revenueSales + revenueDirectCredits;

    // 2. Direct Costs (Cost of Goods Sold / Purchases)
    const costPurchases = filteredPurchases.reduce((sum, pur) => sum + Number(pur.Total_Amount || 0), 0);
    const grossProfit = totalRevenue - costPurchases;

    // 3. Operating Expenses
    const directExpenses = filteredRows
      .filter(r => r.mapped_expense_head_id)
      .reduce((sum, r) => sum + Number(r.debit || 0), 0);

    const headsBreakdown = expenseHeads
      .filter(h => !selectedCompanyId || !h.company_id || String(h.company_id) === String(selectedCompanyId))
      .map(h => {
        const amount = filteredRows
          .filter(r => r.mapped_expense_head_id === h.id)
          .reduce((sum, r) => sum + Number(r.debit || 0), 0);
        return { name: h.name, amount };
      })
      .filter(h => h.amount > 0);

    // Other/Direct unmapped bank expenses (general and admin expenses)
    const generalExpenses = filteredRows
      .filter(r => r.debit > 0 && !r.mapped_expense_head_id && !r.mapped_purchase_id && !r.mapped_loan_id && !r.mapped_fd_id)
      .reduce((sum, r) => sum + Number(r.debit || 0), 0);

    // Loan repayments (treated as outflow/expenditure for direct P&L estimation)
    const financeExpenses = filteredRows
      .filter(r => r.mapped_loan_id)
      .reduce((sum, r) => sum + Number(r.debit || 0), 0);

    const totalOperatingExpenses = directExpenses + generalExpenses + financeExpenses;
    const netProfit = grossProfit - totalOperatingExpenses;

    return {
      revenueSales,
      revenueDirectCredits,
      totalRevenue,
      costPurchases,
      grossProfit,
      headsBreakdown,
      directExpenses,
      generalExpenses,
      financeExpenses,
      totalOperatingExpenses,
      netProfit
    };
  }, [selectedCompanyId, selectedYear, companies, expenseHeads, rows, invoices, purchases]);

  return (
    <div className="bg-white shadow-xl border border-blue-100 rounded-3xl p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h3 className="font-extrabold text-blue-900 text-xl">Profit & Loss Statement</h3>
          <p className="text-gray-500 text-xs">Estimated revenue, cost of goods, overheads, and net profit margins.</p>
        </div>

        <div className="flex flex-wrap gap-3">
          <select
            value={selectedCompanyId}
            onChange={e => setSelectedCompanyId(e.target.value)}
            className="form-select border border-gray-300 rounded-xl px-3 py-2 text-xs font-semibold bg-white text-slate-800 focus:outline-none"
          >
            <option value="">All Companies</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.company_name}</option>
            ))}
          </select>

          <select
            value={selectedYear}
            onChange={e => setSelectedYear(e.target.value)}
            className="form-select border border-gray-300 rounded-xl px-3 py-2 text-xs font-semibold bg-white text-slate-800 focus:outline-none"
          >
            <option value="">All Years</option>
            {availableYears.map(yr => (
              <option key={yr} value={yr}>{yr}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-500 font-medium text-sm">Generating P&L ledger...</div>
      ) : (
        <div className="max-w-3xl mx-auto border border-blue-50 rounded-2xl overflow-hidden shadow-sm">
          <div className="bg-slate-50 border-b border-blue-100 px-6 py-4">
            <h4 className="font-extrabold text-blue-950 text-sm text-center tracking-wide uppercase">Statement of Income & Expenses</h4>
          </div>

          <div className="p-6 space-y-6">
            {/* 1. Revenue */}
            <div>
              <div className="flex justify-between font-black text-slate-900 border-b-2 border-slate-200 pb-1 text-sm uppercase">
                <span>I. REVENUE FROM OPERATIONS</span>
                <span>₹{plData.totalRevenue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="pl-4 py-2 space-y-1 text-xs">
                <div className="flex justify-between text-slate-600 font-semibold">
                  <span>Sales Revenue (Invoices Generated)</span>
                  <span>₹{plData.revenueSales.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-slate-600 font-semibold">
                  <span>Other Indirect Credits / Receipts</span>
                  <span>₹{plData.revenueDirectCredits.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>

            {/* 2. Direct Costs */}
            <div>
              <div className="flex justify-between font-black text-slate-900 border-b-2 border-slate-200 pb-1 text-sm uppercase">
                <span>II. LESS: DIRECT COSTS / PURCHASES</span>
                <span className="text-red-600">(₹{plData.costPurchases.toLocaleString('en-IN', { minimumFractionDigits: 2 })})</span>
              </div>
              <div className="pl-4 py-2 text-xs">
                <div className="flex justify-between text-slate-600 font-semibold">
                  <span>Cost of Purchases (Vendor Bills)</span>
                  <span>₹{plData.costPurchases.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>

            {/* Gross Margin */}
            <div className="bg-slate-50 rounded-xl px-4 py-2.5 flex justify-between font-bold text-slate-900 text-xs border border-slate-100 uppercase">
              <span>Gross Profit (I - II)</span>
              <span className={plData.grossProfit >= 0 ? "text-emerald-700 font-black" : "text-rose-700 font-black"}>
                ₹{plData.grossProfit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>

            {/* 3. Overheads */}
            <div>
              <div className="flex justify-between font-black text-slate-900 border-b-2 border-slate-200 pb-1 text-sm uppercase">
                <span>III. LESS: OPERATING OVERHEADS & EXPENSES</span>
                <span className="text-red-600">(₹{plData.totalOperatingExpenses.toLocaleString('en-IN', { minimumFractionDigits: 2 })})</span>
              </div>
              <div className="pl-4 py-2 space-y-1.5 text-xs">
                {plData.headsBreakdown.map((h, idx) => (
                  <div key={idx} className="flex justify-between text-slate-600 font-semibold">
                    <span>{h.name}</span>
                    <span>₹{h.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
                {plData.financeExpenses > 0 && (
                  <div className="flex justify-between text-slate-600 font-semibold">
                    <span>Finance Charges / Loan Outflows</span>
                    <span>₹{plData.financeExpenses.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                {plData.generalExpenses > 0 && (
                  <div className="flex justify-between text-slate-600 font-semibold">
                    <span>General Admin & Bank Charges</span>
                    <span>₹{plData.generalExpenses.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Net Margin */}
            <div className="bg-blue-900 text-white rounded-2xl px-6 py-4 flex justify-between font-extrabold text-sm uppercase shadow-md">
              <span>Net Profit / Earnings</span>
              <span className={plData.netProfit >= 0 ? "text-emerald-300 font-black text-base" : "text-rose-300 font-black text-base"}>
                {plData.netProfit < 0 ? "-" : ""}₹{Math.abs(plData.netProfit).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitLossReport;
